/**
 * PhoneOTPModal.tsx
 *
 * Bottom sheet модалка з двома кроками:
 *   step 'phone' → введення номеру → auth({ ident_field, ident_value })
 *   step 'otp'   → введення коду  → confirmAuthOTP({ ident_field, ident_value, otp_code })
 *                                  → setTokens + scheduleRefresh + getPlayerData
 *                                  → після playerData — закрити модалку, onPay(pendingPsp)
 *
 * Використовує ті самі actions що і OTPContainer:
 *   auth, confirmAuthOTP, getPlayerData, refreshPlayerData, scheduleRefresh
 */

import React, { FC, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Template } from '../Quicktip/helpers/quicktipHelpers';
import { setTokens, scheduleRefresh } from '../../../services/tokenService';
import { useAction } from '../../../store/hooks/useAction';
import { useTypedSelector } from '../../../store/hooks/useTypedSelector';

interface PhoneOTPModalProps {
    show:          boolean;
    onClose:       () => void;
    styles:        Template;
    operator:      string;
    placeholder?:  string;
    msisdn:        string;
    onPhoneChange: (phone: string) => void;
    onVerified:    () => void;
}

type ModalStep   = 'phone' | 'otp' | 'credentials';
type AuthMode    = 'phone-otp' | 'email-password' | 'phone-password';

/* ─── tiny icons ─── */
const SendIcon = ({ color }: { color: string }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"
              stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const ArrowLeftIcon = ({ color }: { color: string }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M19 12H5M12 19l-7-7 7-7" stroke={color} strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const CloseIcon = ({ color }: { color: string }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M18 6L6 18M6 6l12 12" stroke={color} strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

/* ─── step dots ─── */
const StepDots: FC<{ step: ModalStep; mode: AuthMode; accent: string; fontColor: string }> = ({ step, mode, accent, fontColor }) => {
    const steps: ModalStep[] = mode === 'phone-otp' ? ['phone', 'otp'] : ['credentials'];
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '24px' }}>
            {steps.map((s) => {
                const isActive = s === step || (s === 'credentials' && step === 'credentials');
                const isDone   = s === 'phone' && step === 'otp';
                return (
                    <div key={s} style={{
                        height:       '4px',
                        width:        isActive ? '24px' : '8px',
                        borderRadius: '2px',
                        background:   isActive || isDone ? accent : `${fontColor}25`,
                        transition:   'width .25s, background .25s',
                    }} />
                );
            })}
        </div>
    );
};

/* ─── OTP boxes ─── */
const OTP_LENGTH = 4;

const OTPBoxes: FC<{
    value:      string;
    onChange:   (v: string) => void;
    hasError:   boolean;
    accent:     string;
    fontColor:  string;
    fieldBg:    string;
    fieldColor: string;
}> = ({ value, onChange, hasError, accent, fontColor, fieldBg, fieldColor }) => {
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

    const digits = value.split('').concat(Array(OTP_LENGTH).fill('')).slice(0, OTP_LENGTH);

    const focusAt = (i: number) => inputsRef.current[i]?.focus();

    const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const next = digits.map((d, idx) => idx === i ? '' : d).join('');
            onChange(next.slice(0, i) + next.slice(i + 1));
            if (i > 0) focusAt(i - 1);
        }
    };

    const handleInput = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const char = e.target.value.replace(/\D/g, '').slice(-1);
        const arr  = digits.slice();
        arr[i]     = char;
        onChange(arr.join(''));
        if (char && i < OTP_LENGTH - 1) focusAt(i + 1);
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
        onChange(pasted);
        focusAt(Math.min(pasted.length, OTP_LENGTH - 1));
    };

    return (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
            {digits.map((d, i) => (
                <input
                    key={i}
                    ref={el => { inputsRef.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => handleInput(i, e)}
                    onKeyDown={e => handleKey(i, e)}
                    onPaste={handlePaste}
                    style={{
                        flex:          1,
                        height:        '52px',
                        borderRadius:  '10px',
                        border:        hasError
                            ? '1.5px solid #e74c3c'
                            : d
                                ? `1.5px solid ${accent}`
                                : `0.5px solid ${fontColor}25`,
                        background:    d ? `color-mix(in srgb, ${accent} 8%, ${fieldBg})` : fieldBg,
                        color:         fieldColor,
                        fontSize:      '20px',
                        fontWeight:    600,
                        textAlign:     'center',
                        outline:       'none',
                        caretColor:    'transparent',
                        transition:    'border-color .15s, background .15s',
                        minWidth:      0,
                    } as React.CSSProperties}
                />
            ))}
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════════════════ */
const PhoneOTPModal: FC<PhoneOTPModalProps> = ({
                                                   show, onClose, styles, operator, placeholder,
                                                   msisdn, onPhoneChange, onVerified,
                                               }) => {
    const { t } = useTranslation();
    const { confirmAuthOTP, getPlayerData, refreshPlayerData, auth } = useAction();

    const { confirmAuthOTPData, confirmAuthOTPError, confirmAuthOTPLoading } =
        useTypedSelector((state) => state.confirmAuthOTPReducer);
    const { playerData, loading: playerLoading } =
        useTypedSelector((state) => state.getPlayerReducer);
    const { refreshPlayerData: refreshData } =
        useTypedSelector((state) => state.refreshPlayerReducer);

    const [step, setStep]               = useState<ModalStep>('phone');
    const [authMode, setAuthMode]       = useState<AuthMode>('phone-otp');
    const [otpCode, setOtpCode]         = useState('');
    const [localPhone, setLocalPhone]   = useState(msisdn);
    const [localEmail, setLocalEmail]   = useState('');
    const [localPassword, setLocalPassword] = useState('');
    const [showPassword, setShowPassword]   = useState(false);
    const [error, setError]             = useState<string | null>(null);
    const [authLoading, setAuthLoading] = useState(false);
    const [resendSeconds, setResendSeconds] = useState(0);

    const accent     = styles.colors.buttonBGColor;
    const bg         = styles.colors.blockBgColor;
    const fontColor  = styles.colors.fontColor;
    const fieldBg    = styles.colors.fieldsBgColor;
    const fieldColor = styles.colors.fieldsFontColor;
    const btnColor   = styles.colors.buttonFontColor;
    const titleFont  = styles.fonts.titleFontFamily;

    // reset on open
    useEffect(() => {
        if (show) {
            setStep('phone');
            setOtpCode('');
            setError(null);
            setLocalPhone(msisdn);
            setLocalEmail('');
            setLocalPassword('');
            setShowPassword(false);
        }
    }, [show]);

    // [STEP 2] confirmAuthOTPData → setTokens + getPlayerData
    useEffect(() => {
        if (!confirmAuthOTPData?.access_token) return;
        const { access_token, refresh_token, access_token_expires_in } = confirmAuthOTPData;
        setTokens({ access_token, refresh_token, access_token_expires_in });
        scheduleRefresh(async () => { await refreshPlayerData({ refresh_token }); });
        getPlayerData(access_token);
    }, [confirmAuthOTPData]);

    // [STEP 3] refresh tokens
    useEffect(() => {
        if (!refreshData?.access_token) return;
        const { access_token, refresh_token, access_token_expires_in } = refreshData;
        setTokens({ access_token, refresh_token, access_token_expires_in });
        scheduleRefresh(async () => { await refreshPlayerData({ refresh_token }); });
    }, [refreshData]);

    // [STEP 4] playerData → close + onVerified
    useEffect(() => {
        if (!playerData || Object.keys(playerData).length === 0) return;
        if (!show) return;
        onVerified();
        onClose();
    }, [playerData]);

    // OTP error
    useEffect(() => {
        if (!confirmAuthOTPError) return;
        setError(
            typeof confirmAuthOTPError === 'string'
                ? confirmAuthOTPError
                : (confirmAuthOTPError as any)?.message ?? t('notifications.otpError'),
        );
        setOtpCode('');
    }, [confirmAuthOTPError]);

    // auto-submit when all 6 digits filled
    useEffect(() => {
        if (otpCode.length === OTP_LENGTH && step === 'otp') {
            handleOtpSubmit();
        }
    }, [otpCode]);

    const handlePhoneSubmit = () => {
        const phone = localPhone.trim();
        if (phone.length < 6) { setError(t('notifications.missedPhone')); return; }
        setError(null);
        onPhoneChange(phone);
        auth({ ident_field: 'phone', ident_value: phone });
        setStep('otp');
        setResendSeconds(30);
        const interval = setInterval(() => {
            setResendSeconds(prev => {
                if (prev <= 1) { clearInterval(interval); return 0; }
                return prev - 1;
            });
        }, 1000);
    };

    const handleOtpSubmit = () => {
        if (otpCode.length < 4) { setError(t('notifications.missedOTPCode')); return; }
        setError(null);
        confirmAuthOTP({ ident_field: 'phone', ident_value: localPhone.trim(), otp_code: otpCode });
    };

    const handleResend = async () => {
        try {
            await axios.post('/api/auth/resend', { msisdn: localPhone.trim() });
            setOtpCode('');
            setError(null);
            setResendSeconds(30);
            const interval = setInterval(() => {
                setResendSeconds(prev => {
                    if (prev <= 1) { clearInterval(interval); return 0; }
                    return prev - 1;
                });
            }, 1000);
        } catch (err: any) {
            setError(err.response?.data?.message ?? t('notifications.resendError'));
        }
    };

    const handleCredentialsSubmit = async () => {
        const ident = authMode === 'email-password' ? localEmail.trim() : localPhone.trim();
        if (!ident) { setError(authMode === 'email-password' ? t('notifications.missedEmail', 'Enter your email') : t('notifications.missedPhone')); return; }
        if (!localPassword) { setError(t('notifications.missedPassword', 'Enter your password')); return; }
        setError(null);
        try {
            setAuthLoading(true);
            const field = authMode === 'email-password' ? 'email' : 'phone';
            const response = await import('axios').then(m => m.default.post('/api/auth/login', {
                ident_field: field,
                ident_value: ident,
                password:    localPassword,
            }));
            const { access_token, refresh_token, access_token_expires_in } = response.data;
            setTokens({ access_token, refresh_token, access_token_expires_in });
            scheduleRefresh(async () => { await refreshPlayerData({ refresh_token }); });
            getPlayerData(access_token);
        } catch (err: any) {
            setError(err.response?.data?.message ?? t('notifications.loginError', 'Invalid credentials'));
        } finally {
            setAuthLoading(false);
        }
    };

    if (!show) return null;

    const isLoading      = confirmAuthOTPLoading || playerLoading || authLoading;
    const isCredMode     = authMode !== 'phone-otp';
    const phoneReady     = !isLoading && localPhone.trim().length >= 6;
    const otpReady       = !isLoading && otpCode.length >= 4;
    const maskedPhone    = localPhone.length > 4
        ? localPhone.slice(0, -4).replace(/\d/g, '•') + localPhone.slice(-4)
        : localPhone;

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                style={{
                    position:       'fixed',
                    inset:          0,
                    background:     'rgba(0,0,0,.6)',
                    zIndex:         1000,
                    backdropFilter: 'blur(3px)',
                }}
            />

            {/* Sheet */}
            <div style={{
                position:     'fixed',
                bottom:       0,
                left:         0,
                right:        0,
                zIndex:       1001,
                background:   bg,
                borderRadius: '20px 20px 0 0',
                padding:      '12px 20px 40px',
                boxShadow:    '0 -8px 40px rgba(0,0,0,.22)',
                animation:    'slideUp .28s cubic-bezier(.32,.72,0,1)',
            }}>

                {/* handle */}
                <div style={{
                    width:        '36px',
                    height:       '4px',
                    borderRadius: '2px',
                    background:   `${fontColor}20`,
                    margin:       '0 auto 20px',
                }} />

                {/* close button */}
                <button
                    onClick={onClose}
                    style={{
                        position:   'absolute',
                        top:        '16px',
                        right:      '16px',
                        width:      '32px',
                        height:     '32px',
                        borderRadius: '50%',
                        border:     `0.5px solid ${fontColor}20`,
                        background: `${fontColor}0A`,
                        cursor:     'pointer',
                        display:    'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CloseIcon color={fontColor} />
                </button>

                {/* step dots */}
                <StepDots step={step} mode={authMode} accent={accent} fontColor={fontColor} />

                {/* ── MODE SWITCHER ── */}
                {step !== 'otp' && (
                    <div style={{
                        display:       'flex',
                        gap:           '6px',
                        marginBottom:  '20px',
                        background:    `${fontColor}08`,
                        borderRadius:  '12px',
                        padding:       '4px',
                    }}>
                        {([
                            { key: 'phone-otp',       label: 'OTP' },
                            { key: 'email-password',  label: 'Email' },
                            { key: 'phone-password',  label: 'Password' },
                        ] as { key: AuthMode; label: string }[]).map(({ key, label }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => {
                                    setAuthMode(key);
                                    setStep(key === 'phone-otp' ? 'phone' : 'credentials');
                                    setError(null);
                                    setOtpCode('');
                                }}
                                style={{
                                    flex:          1,
                                    padding:       '7px 4px',
                                    borderRadius:  '8px',
                                    border:        'none',
                                    background:    authMode === key ? bg : 'transparent',
                                    color:         authMode === key ? fontColor : `${fontColor}55`,
                                    fontSize:      '12px',
                                    fontWeight:    authMode === key ? 600 : 400,
                                    cursor:        'pointer',
                                    transition:    'all .18s',
                                    boxShadow:     authMode === key ? `0 1px 4px ${fontColor}15` : 'none',
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── STEP: credentials (email-password / phone-password) ── */}
                {step === 'credentials' && (
                    <>
                        <h3 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: 700, textAlign: 'center', color: fontColor, fontFamily: titleFont }}>
                            {authMode === 'email-password'
                                ? t('auth.signInEmail', 'Sign in with Email')
                                : t('auth.signInPhone', 'Sign in with Phone')}
                        </h3>
                        <p style={{ margin: '0 0 20px', fontSize: '14px', color: fontColor, textAlign: 'center', opacity: .5, lineHeight: 1.5 }}>
                            {t('auth.enterCredentials', 'Enter your credentials to access your account.')}
                        </p>

                        {/* ident field */}
                        <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: fontColor, opacity: .45, marginBottom: '8px' }}>
                            {authMode === 'email-password' ? t('auth.email', 'Email') : t('auth.phoneNumber', 'Phone number')}
                        </div>
                        <input
                            type={authMode === 'email-password' ? 'email' : 'tel'}
                            value={authMode === 'email-password' ? localEmail : localPhone}
                            onChange={(e) => {
                                if (authMode === 'email-password') setLocalEmail(e.target.value);
                                else setLocalPhone(e.target.value);
                                setError(null);
                            }}
                            placeholder={authMode === 'email-password' ? 'email' : (placeholder ?? '+254...')}
                            autoFocus
                            style={{
                                width: '100%', padding: '14px 16px', borderRadius: '12px',
                                border: error ? '1.5px solid #e74c3c' : `0.5px solid ${fontColor}25`,
                                background: fieldBg, color: fieldColor, fontSize: '16px',
                                outline: 'none', marginBottom: '10px', boxSizing: 'border-box',
                            } as React.CSSProperties}
                        />

                        {/* password field */}
                        <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: fontColor, opacity: .45, marginBottom: '8px' }}>
                            {t('auth.password', 'Password')}
                        </div>
                        <div style={{ position: 'relative', marginBottom: error ? '6px' : '20px' }}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={localPassword}
                                onChange={(e) => { setLocalPassword(e.target.value); setError(null); }}
                                placeholder="••••••••"
                                style={{
                                    width: '100%', padding: '14px 44px 14px 16px', borderRadius: '12px',
                                    border: error ? '1.5px solid #e74c3c' : `0.5px solid ${fontColor}25`,
                                    background: fieldBg, color: fieldColor, fontSize: '16px',
                                    outline: 'none', boxSizing: 'border-box',
                                } as React.CSSProperties}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(p => !p)}
                                style={{
                                    position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: fontColor, opacity: .4, fontSize: '13px', padding: 0,
                                }}
                            >
                                {showPassword ? '🙈' : '👁'}
                            </button>
                        </div>

                        {error && <p style={{ margin: '0 0 14px', fontSize: '12px', color: '#e74c3c' }}>{error}</p>}

                        <button
                            type="button"
                            disabled={isLoading}
                            onClick={handleCredentialsSubmit}
                            style={{
                                width: '100%', padding: '15px', borderRadius: '12px', border: 'none',
                                background: isLoading ? `${accent}50` : accent,
                                color: btnColor, fontSize: '15px', fontWeight: 600,
                                cursor: isLoading ? 'not-allowed' : 'pointer', transition: 'background .15s',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                            }}
                        >
                            {isLoading ? t('auth.signingIn', 'Signing in...') : t('auth.signIn', 'Sign in')}
                        </button>
                    </>
                )}

                {/* ── STEP: phone ── */}
                {step === 'phone' && authMode === 'phone-otp' && (
                    <>
                        <h3 style={{
                            margin:     '0 0 6px',
                            fontSize:   '20px',
                            fontWeight: 700,
                            color:      fontColor,
                            fontFamily: titleFont,
                            textAlign:   'center',
                        }}>
                            {t('auth.enterPhone', 'Enter your phone number')}
                        </h3>
                        <p style={{
                            margin:     '0 0 24px',
                            fontSize:   '14px',
                            color:      fontColor,
                            textAlign:   'center',
                            opacity:    .5,
                            lineHeight: 1.5,
                        }}>
                            {t('auth.phoneDesc', "We'll send you a one-time code to verify your number.")}
                        </p>

                        {/* field label */}
                        <div style={{
                            fontSize:      '11px',
                            fontWeight:    600,
                            letterSpacing: '.07em',
                            textTransform: 'uppercase',
                            color:         fontColor,
                            opacity:       .45,
                            marginBottom:  '8px',
                        }}>
                            {t('auth.phoneNumber', 'Phone number')}
                        </div>

                        {/* phone input */}
                        <input
                            type="tel"
                            value={localPhone}
                            onChange={(e) => { setLocalPhone(e.target.value); setError(null); }}
                            placeholder={placeholder ?? t('quickbet.priceBlock.inputPhonePlaceholder')}
                            maxLength={20}
                            minLength={9}
                            autoFocus
                            style={{
                                width:        '100%',
                                padding:      '14px 16px',
                                borderRadius: '12px',
                                border:       error
                                    ? '1.5px solid #e74c3c'
                                    : `0.5px solid ${fontColor}25`,
                                background:   fieldBg,
                                color:        fieldColor,
                                fontSize:     '16px',
                                outline:      'none',
                                marginBottom: error ? '6px' : '6px',
                                boxSizing:    'border-box',
                                transition:   'border-color .15s',
                            } as React.CSSProperties}
                        />

                        {error && (
                            <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#e74c3c' }}>
                                {error}
                            </p>
                        )}

                        <p style={{
                            margin:     '6px 0 20px',
                            fontSize:   '12px',
                            color:      fontColor,
                            opacity:    .4,
                            lineHeight: 1.5,
                        }}>
                            {t('auth.noAccountNeeded', "We'll send a one-time code. No account needed.")}
                        </p>

                        <button
                            type="button"
                            disabled={!phoneReady}
                            onClick={handlePhoneSubmit}
                            style={{
                                width:         '100%',
                                padding:       '15px',
                                borderRadius:  '12px',
                                border:        'none',
                                background:    phoneReady ? accent : `${accent}50`,
                                color:         btnColor,
                                fontSize:      '15px',
                                fontWeight:    600,
                                cursor:        phoneReady ? 'pointer' : 'not-allowed',
                                display:       'flex',
                                alignItems:    'center',
                                justifyContent:'center',
                                gap:           '8px',
                                transition:    'background .15s',
                            }}
                        >
                            <SendIcon color={btnColor} />
                            {isLoading
                                ? t('auth.sending', 'Sending...')
                                : t('auth.sendCode', 'Send verification code')
                            }
                        </button>
                    </>
                )}

                {/* ── STEP: otp ── */}
                {step === 'otp' && (
                    <>
                        {/* back link */}
                        <button
                            type="button"
                            onClick={() => { setStep('phone'); setOtpCode(''); setError(null); }}
                            style={{
                                display:    'flex',
                                alignItems: 'center',
                                gap:        '5px',
                                background: 'none',
                                border:     'none',
                                color:      fontColor,
                                opacity:    .45,
                                fontSize:   '13px',
                                cursor:     'pointer',
                                padding:    '0 0 16px',
                                fontFamily: 'inherit',
                            }}
                        >
                            <ArrowLeftIcon color={fontColor} />
                            {t('auth.back', 'Back')}
                        </button>

                        <h3 style={{
                            margin:     '0 0 6px',
                            fontSize:   '20px',
                            fontWeight: 700,
                            color:      fontColor,
                            fontFamily: titleFont,
                        }}>
                            {t('auth.enterOtp', 'Enter verification code')}
                        </h3>
                        <p style={{
                            margin:     '0 0 24px',
                            fontSize:   '14px',
                            color:      fontColor,
                            opacity:    .5,
                            lineHeight: 1.5,
                        }}>
                            {t('auth.otpSentTo', 'Code sent to')}{' '}
                            <strong style={{ opacity: 1, color: fontColor }}>
                                {maskedPhone}
                            </strong>
                        </p>

                        {/* field label */}
                        <div style={{
                            fontSize:      '11px',
                            fontWeight:    600,
                            letterSpacing: '.07em',
                            textTransform: 'uppercase',
                            color:         fontColor,
                            opacity:       .45,
                            marginBottom:  '10px',
                        }}>
                            {t('auth.verificationCode', 'Verification code')}
                        </div>

                        <OTPBoxes
                            value={otpCode}
                            onChange={(v) => { setOtpCode(v); setError(null); }}
                            hasError={!!error}
                            accent={accent}
                            fontColor={fontColor}
                            fieldBg={fieldBg}
                            fieldColor={fieldColor}
                        />

                        {error && (
                            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#e74c3c' }}>
                                {error}
                            </p>
                        )}

                        {/* resend row */}
                        <div style={{
                            display:        'flex',
                            alignItems:     'center',
                            justifyContent: 'space-between',
                            margin:         '12px 0 20px',
                        }}>
                            {resendSeconds > 0 ? (
                                <span style={{ fontSize: '13px', color: fontColor, opacity: .4 }}>
                                    {t('otp.resendIn', 'Resend in')}{' '}
                                    <strong style={{ opacity: .7 }}>{resendSeconds}s</strong>
                                </span>
                            ) : (
                                <span style={{ fontSize: '13px', color: fontColor, opacity: .35 }}>
                                    {t('otp.didntReceive', "Didn't receive it?")}
                                </span>
                            )}
                            <button
                                id="OPTResendButton"
                                type="button"
                                disabled={resendSeconds > 0}
                                onClick={handleResend}
                                style={{
                                    background: 'none',
                                    border:     'none',
                                    color:      resendSeconds > 0 ? `${fontColor}30` : accent,
                                    fontSize:   '13px',
                                    fontWeight: 500,
                                    cursor:     resendSeconds > 0 ? 'not-allowed' : 'pointer',
                                    fontFamily: 'inherit',
                                    padding:    0,
                                }}
                            >
                                {t('otp.resendButton', 'Resend code')}
                            </button>
                        </div>

                        <button
                            type="button"
                            disabled={!otpReady}
                            onClick={handleOtpSubmit}
                            style={{
                                width:          '100%',
                                padding:        '15px',
                                borderRadius:   '12px',
                                border:         'none',
                                background:     otpReady ? accent : `${accent}50`,
                                color:          btnColor,
                                fontSize:       '15px',
                                fontWeight:     600,
                                cursor:         otpReady ? 'pointer' : 'not-allowed',
                                transition:     'background .15s',
                                letterSpacing:  '.01em',
                            }}
                        >
                            {isLoading
                                ? t('auth.verifying', 'Verifying...')
                                : t('auth.confirm', 'Confirm')
                            }
                        </button>
                    </>
                )}
            </div>

            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
            `}</style>
        </>
    );
};

export default PhoneOTPModal;